// routes/events/index.tsx
// -----------------------------------------------------------------------------
// Events page - displays all events for the logged-in user across all clubs.
// Supports filters: Upcoming, Hosting, Attended. Groups events by month.
// -----------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { requireAuth } from "@/lib/auth";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import {
  useFetchAllEventsQuery,
  useFetchAllClubMembersQuery,
  useFetchUserProfilesByIdsQuery,
  useDeleteEventMutation,
  EventType,
  type EventOccurrence,
  type Event,
} from "@/api/clubsApi";
import { isOwnerOrCohost } from "@tinyclub/shared/models";
import type { EventCardHost } from "@/components/events/EventCard";
import { useAuth } from "@/components/providers/AuthProvider";
import { EventCard } from "@/components/events/EventCard";
import { MonthDivider } from "@/components/events/MonthDivider";
import { EventDetailSidePanel, EventFormContent } from "@/components/events";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/AlertDialog";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/components/ui/cn";
import { groupEventsByMonth } from "@/utils/eventGrouping";

export const Route = createFileRoute("/events/")({
  beforeLoad: requireAuth,
  component: EventsPage,
});

type EventFilter = "upcoming" | "hosting" | "attended";

function EventsPage() {
  const { user } = useAuth();
  const currentUserId = user?.id;

  useHeaderConfig(
    {
      breadcrumbs: [{ label: "Events" }],
    },
    []
  );

  const [filter, setFilter] = useState<EventFilter>("upcoming");
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Edit mode state
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editingClubId, setEditingClubId] = useState<string | null>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);

  // Delete state
  const [deletingEvent, setDeletingEvent] = useState<EventOccurrence | null>(null);
  const [deleteEvent] = useDeleteEventMutation();

  // Fetch all events (include past for "Attended" filter, include reactions for hang cards)
  const {
    data: eventsData,
    isLoading,
    isFetching,
    error,
  } = useFetchAllEventsQuery(
    currentUserId
      ? {
          includeAttendees: true,
          includeReactions: true,
          includePast: filter === "attended",
          userId: currentUserId,
        }
      : { includeAttendees: true, includeReactions: true },
    { skip: !currentUserId }
  );

  // Fetch user's club memberships (for "Hosting" filter)
  const { data: myMemberships } = useFetchAllClubMembersQuery(undefined, {
    skip: !currentUserId || filter !== "hosting",
  });

  // Build a set of club IDs where user is owner/cohost
  const ownerCohostClubIds = useMemo(() => {
    if (!myMemberships) return new Set<string>();
    const ids = new Set<string>();
    for (const m of myMemberships) {
      if (isOwnerOrCohost(m.role)) {
        ids.add(m.club_id);
      }
    }
    return ids;
  }, [myMemberships]);

  // Collect unique creator IDs from events to fetch their profiles
  const creatorIds = useMemo(() => {
    if (!eventsData) return [];
    const ids = new Set<string>();
    for (const event of eventsData) {
      if (event.rootEvent?.creator_id) {
        ids.add(event.rootEvent.creator_id);
      }
    }
    return Array.from(ids);
  }, [eventsData]);

  const { data: creatorProfiles } = useFetchUserProfilesByIdsQuery(creatorIds, {
    skip: creatorIds.length === 0,
  });

  // Build hosts map: event ID -> EventCardHost[]
  const hostsMap = useMemo(() => {
    const map = new Map<string, EventCardHost[]>();
    if (!eventsData || !creatorProfiles) return map;

    const profileMap = new Map(creatorProfiles.map((p) => [p.id, p]));

    for (const event of eventsData) {
      const rootEvent = event.rootEvent;
      if (!rootEvent?.creator_id) continue;

      const profile = profileMap.get(rootEvent.creator_id);
      if (profile) {
        map.set(event.id, [
          {
            userId: rootEvent.creator_id,
            firstName: profile.first_name || "Unknown",
            lastName: profile.last_name || "",
            avatarUrl: profile.avatar_small || profile.avatar_medium || undefined,
          },
        ]);
      }
    }
    return map;
  }, [eventsData, creatorProfiles]);

  // Filter events based on selected filter
  const filteredEvents = useMemo(() => {
    if (!eventsData) return [];
    const now = DateTime.now();

    switch (filter) {
      case "upcoming":
        // Already filtered to upcoming by the API (includePast=false)
        return eventsData;

      case "hosting":
        return eventsData.filter((event) => {
          const rootEvent = event.rootEvent;
          if (!rootEvent) return false;
          // Creator
          if (rootEvent.creator_id === currentUserId) return true;
          // Owner/cohost in the club
          return rootEvent.club_id ? ownerCohostClubIds.has(rootEvent.club_id) : false;
        });

      case "attended":
        // Past events (not hangs) where user RSVP'd "yes"
        return eventsData.filter((event) => {
          const rootEvent = event.rootEvent;
          if (!rootEvent) return false;
          if (rootEvent.event_type === EventType.hang) return false;
          const isPast = event.occurrence_end
            ? DateTime.fromISO(event.occurrence_end) < now
            : DateTime.fromISO(event.occurrence_start) < now;
          if (!isPast) return false;
          const attendees = event.event_attendees || [];
          return attendees.some(
            (a) => a.user_id === currentUserId && a.rsvp_status === "yes"
          );
        });

      default:
        return eventsData;
    }
  }, [eventsData, filter, currentUserId, ownerCohostClubIds]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  // Search filtering (applied after tab filter, before month grouping)
  const searchFilteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return filteredEvents;
    const q = searchQuery.toLowerCase().trim();
    return filteredEvents.filter((event) => {
      const root = event.rootEvent;
      if (!root) return false;
      const title = (root.title || "").toLowerCase();
      const location = (
        root.location_name ||
        root.location_object?.properties?.name_preferred ||
        root.location_object?.properties?.name ||
        ""
      ).toLowerCase();
      const description = (root.description || "").toLowerCase();
      return title.includes(q) || location.includes(q) || description.includes(q);
    });
  }, [filteredEvents, searchQuery]);

  // Group by month (reverse for attended = newest first)
  const monthGroups = useMemo(
    () => groupEventsByMonth(searchFilteredEvents, { reverse: filter === "attended" }),
    [searchFilteredEvents, filter]
  );

  const hasEvents = monthGroups.length > 0;

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-6 lg:pl-20">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <h1 className="text-h3 text-text-primary mb-6">Events</h1>

            {/* Filter pills + search */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex gap-2">
                {(
                  [
                    { key: "upcoming", label: "Upcoming" },
                    { key: "hosting", label: "Hosting" },
                    { key: "attended", label: "Attended" },
                  ] as const
                ).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setFilter(key);
                      setSearchQuery("");
                      setSearchOpen(false);
                    }}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-body-3-medium transition-colors",
                      filter === key
                        ? "bg-text-primary text-text-reversed"
                        : "bg-surface-1 text-text-secondary hover:bg-surface-2"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Animated search */}
              <div
                className={cn(
                  "bg-surface-1 rounded-full flex items-center transition-all duration-200 ease-out overflow-hidden h-8",
                  searchOpen
                    ? "w-[240px] px-3 gap-2"
                    : "w-8 justify-center cursor-pointer"
                )}
                onClick={() => {
                  if (!searchOpen) setSearchOpen(true);
                }}
              >
                <Icon
                  name="search"
                  size={14}
                  className="text-text-secondary flex-shrink-0"
                />
                {searchOpen && (
                  <>
                    <input
                      ref={searchInputRef}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="bg-transparent text-body-3-medium text-text-primary placeholder:text-text-tertiary flex-1 outline-none min-w-0"
                      autoFocus
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchQuery("");
                        setSearchOpen(false);
                      }}
                      className="text-text-tertiary hover:text-text-secondary flex-shrink-0"
                    >
                      <Icon name="xmark" size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {isFetching && !hasEvents && <EventsPageSkeleton />}

            {error != null && !isFetching && (
              <div className="bg-surface-1 rounded-lg border border-border-tertiary p-8 text-center">
                <p className="text-body-2 text-text-secondary">
                  Unable to load events. Please try again.
                </p>
              </div>
            )}

            {!isFetching && !error && !hasEvents && <EventsEmptyState filter={filter} />}

            {!isLoading && !error && hasEvents && (
              <div className="space-y-6">
                {monthGroups.map((group) => (
                  <section key={`${group.year}-${group.month}`}>
                    <MonthDivider label={group.monthLabel} className="mb-4" />
                    <div className="space-y-4">
                      {group.events.map((event) => {
                        const isCreator = event.rootEvent?.creator_id === currentUserId;
                        const isHosting =
                          isCreator ||
                          (event.rootEvent?.club_id
                            ? ownerCohostClubIds.has(event.rootEvent.club_id)
                            : false);

                        return (
                          <button
                            key={event.id}
                            onClick={() => setSelectedEventId(event.id)}
                            className="block w-full text-left"
                          >
                            <EventCard
                              event={event}
                              currentUserId={currentUserId}
                              hosts={hostsMap.get(event.id)}
                              isHosting={isHosting}
                              canEdit={isCreator}
                              canDelete={isCreator}
                              onEdit={() => {
                                if (event.rootEvent) {
                                  setEditingEvent(event.rootEvent);
                                  setEditingClubId(event.rootEvent.club_id);
                                  setEditFormOpen(true);
                                }
                              }}
                              onDelete={() => setDeletingEvent(event)}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </PageTransition>

      {/* Event Detail Side Panel */}
      {selectedEventId && (
        <EventDetailSidePanel
          eventId={selectedEventId}
          open={!!selectedEventId}
          onOpenChange={(open) => {
            if (!open) setSelectedEventId(null);
          }}
        />
      )}

      {/* Edit Event Side Panel */}
      {editingEvent && editingClubId && (
        <SidePanel
          open={editFormOpen}
          onOpenChange={(open) => {
            setEditFormOpen(open);
            if (!open) {
              setEditingEvent(null);
              setEditingClubId(null);
            }
          }}
        >
          <SidePanelContent>
            <SidePanelHeader
              title={
                editingEvent.event_type === EventType.hang
                  ? "Edit Hang"
                  : "Edit Event"
              }
              onClose={() => {
                setEditFormOpen(false);
                setEditingEvent(null);
                setEditingClubId(null);
              }}
            />
            <SidePanelBody>
              <EventFormContent
                clubId={editingClubId}
                creatorId={editingEvent.creator_id ?? ""}
                eventType={
                  editingEvent.event_type === EventType.hang ? "hang" : "event"
                }
                eventId={editingEvent.id}
                existingEvent={editingEvent}
                onClose={() => {
                  setEditFormOpen(false);
                  setEditingEvent(null);
                  setEditingClubId(null);
                }}
                onDeleted={() => {
                  if (selectedEventId) setSelectedEventId(null);
                }}
                showHeader={false}
              />
            </SidePanelBody>
          </SidePanelContent>
        </SidePanel>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingEvent}
        onOpenChange={(open) => {
          if (!open) setDeletingEvent(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deletingEvent?.rootEvent?.title || "this event"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The event and all associated RSVPs
              will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red text-white hover:bg-red/90"
              onClick={async () => {
                if (!deletingEvent?.rootEvent?.club_id) return;
                try {
                  await deleteEvent({
                    eventId: deletingEvent.rootEvent.id,
                    clubId: deletingEvent.rootEvent.club_id,
                  }).unwrap();
                  if (selectedEventId === deletingEvent.id) {
                    setSelectedEventId(null);
                  }
                  setDeletingEvent(null);
                } catch (error) {
                  console.error("Failed to delete event:", error);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function EventsPageSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2].map((i) => (
        <div key={i}>
          <div className="flex items-center gap-4 mb-4">
            <Skeleton className="h-5 w-24" />
            <div className="flex-1 border-t border-border-subtle" />
          </div>
          <div className="space-y-4">
            {[1, 2].map((j) => (
              <div
                key={j}
                className="rounded-[24px] border border-border-tertiary bg-background p-4"
              >
                <div className="flex justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-48" />
                    <div className="border-t border-border-subtle" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="w-[136px] h-[136px] rounded-[8px] flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsEmptyState({ filter }: { filter: EventFilter }) {
  const messages: Record<EventFilter, { title: string; description: string }> = {
    upcoming: {
      title: "No upcoming events",
      description: "Events from your clubs will appear here.",
    },
    hosting: {
      title: "Not hosting any events",
      description: "Events you create or co-host will appear here.",
    },
    attended: {
      title: "No attended events",
      description: "Past events you attended will appear here.",
    },
  };

  const { title, description } = messages[filter];

  return (
    <div className="bg-surface-1 rounded-lg border border-border-tertiary p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-2 flex items-center justify-center">
        <Icon name="calendar" size={32} className="text-text-tertiary" />
      </div>
      <h3 className="text-body-1-medium text-text-primary">{title}</h3>
      <p className="text-body-2 text-text-secondary mt-1">{description}</p>
    </div>
  );
}
